import type { DonationLocationValue } from "@project/donate/components/donation-location-fields";
import {
  emptyDonor,
  type DonorInfo,
  type PaymentInfo,
} from "@project/donate/components/donor-types";
import {
  formatDonationAmount as formatAmount,
  type DonationDonorPayload,
  type DonationDonorRecord,
} from "@project/types/donation-donor";

export type CheckoutHeadStatus = {
  isAlreadyHead: boolean;
  headPhone: string;
};

type CheckoutDonorPayloadArgs = {
  donationType: string;
  donor: DonorInfo;
  location: DonationLocationValue;
  amount: number;
  payOnline: boolean;
  payment: PaymentInfo;
  message?: string;
  headStatus?: CheckoutHeadStatus;
  wants80gReceipt?: boolean;
  keepAnonymousOnDonorWall?: boolean;
};

function mapCheckoutPaymentMode(
  payOnline: boolean,
  payment: PaymentInfo
): string {
  if (payOnline) {
    return "upi";
  }
  const raw = payment.payment_mode.trim().toLowerCase();
  if (raw.includes("upi")) {
    return "upi";
  }
  if (raw.includes("cheque") || raw.includes("demand draft")) {
    return "cheque";
  }
  if (raw.includes("bank") || raw.includes("neft") || raw.includes("rtgs")) {
    return "neft";
  }
  if (raw.includes("cash")) {
    return "cash";
  }
  return "upi";
}

function buildCheckoutDonorFields({
  donationType,
  donor,
  location,
  amount,
  payOnline,
  payment,
  message = "",
  headStatus,
  wants80gReceipt,
  keepAnonymousOnDonorWall,
}: CheckoutDonorPayloadArgs) {
  const isAlreadyHead = Boolean(headStatus?.isAlreadyHead);
  const result: Partial<DonationDonorPayload> = {
    name: donor.name.trim(),
    email: donor.email.trim(),
    phone: donor.mobile.trim(),
    organization: donor.company.trim(),
    donation_type: donationType.trim(),
    amount: formatAmount(amount),
    mode: payOnline ? "online" : "offline",
    payment_mode: mapCheckoutPaymentMode(payOnline, payment),
    pan_number: donor.pan.trim().toUpperCase(),
    message: message.trim(),
    address_line1: donor.address.trim(),
    district: Number(location.district_id),
    city: Number(location.city_id),
    area: Number(location.area_id),
    pincode: donor.pincode.trim(),
    is_head: !isAlreadyHead,
    is_already_head: isAlreadyHead,
    wants_80g_receipt: Boolean(wants80gReceipt),
    keep_anonymous_on_donor_wall: Boolean(keepAnonymousOnDonorWall),
    csr_eligibility: Boolean(donor.csr_eligibility),
  };

  if (isAlreadyHead) {
    result.head_phone = headStatus?.headPhone.trim();
  }

  const chequeNo = payment.cheque_no.trim();
  if (chequeNo) {
    result.by_cheque_no = chequeNo;
  }

  return result;
}

export function buildPublicCheckoutDonorCreate(
  args: CheckoutDonorPayloadArgs
): DonationDonorPayload {
  const fields = buildCheckoutDonorFields(args);
  return {
    name: fields.name ?? "",
    email: fields.email ?? "",
    phone: fields.phone ?? "",
    organization: fields.organization ?? "",
    donation_type: fields.donation_type ?? "",
    amount: fields.amount ?? formatAmount(args.amount),
    mode: fields.mode ?? "online",
    payment_mode: fields.payment_mode ?? "upi",
    pan_number: fields.pan_number ?? "",
    message: fields.message ?? "",
    address_line1: fields.address_line1,
    district: fields.district,
    city: fields.city,
    area: fields.area,
    pincode: fields.pincode,
    by_cheque_no: fields.by_cheque_no,
    is_head: fields.is_head ?? true,
    is_already_head: fields.is_already_head ?? false,
    head_phone: fields.head_phone,
    wants_80g_receipt: fields.wants_80g_receipt,
    keep_anonymous_on_donor_wall: fields.keep_anonymous_on_donor_wall,
    csr_eligibility: fields.csr_eligibility,
  };
}

export function buildPublicCheckoutDonorPatch(
  args: CheckoutDonorPayloadArgs
): Partial<DonationDonorPayload> {
  return buildCheckoutDonorFields(args);
}

export function mapDonorRecordToDonorInfo(record: DonationDonorRecord): {
  donor: DonorInfo;
  location: DonationLocationValue;
} {
  return {
    donor: {
      ...emptyDonor,
      name: record.name ?? "",
      mobile: record.phone ?? "",
      email: record.email ?? "",
      pan: record.pan_number ?? "",
      address: record.address_line1 ?? "",
      pincode: record.pincode ?? "",
      company: record.organization ?? "",
      csr_eligibility: Boolean(record.csr_eligibility),
      city: record.city_name ?? "",
      state: record.state_name ?? "",
    },
    location: {
      district_id: String(record.district_id ?? record.district ?? ""),
      city_id: String(record.city_id ?? record.city ?? ""),
      area_id: String(record.area_id ?? record.area ?? ""),
    },
  };
}

export function buildDonorMessage(
  d: DonorInfo,
  p: PaymentInfo,
  extra: Record<string, string | number> = {}
): string {
  const lines: string[] = [];
  const add = (k: string, v?: string | number | null) => {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      lines.push(`${k}: ${v}`);
    }
  };
  Object.entries(extra).forEach(([k, v]) => add(k, v));
  lines.push("--- Donor Profile ---");
  add("Family", d.family_name);
  add("DOB", d.date_of_birth);
  add("Alt Mobile", d.alt_mobile);
  add("PAN", d.pan);
  add("Aadhaar", d.aadhaar);
  add("Address", d.address);
  add("City", d.city);
  add("State", d.state);
  add("Pincode", d.pincode);
  add("Country", d.country);
  add("CSR Eligibility", d.csr_eligibility ? "Yes" : "No");
  add("Occupation", d.occupation);
  add("Company", d.company);
  lines.push("--- Payment ---");
  add("Mode", p.payment_mode);
  add("Txn / UTR", p.transaction_id);
  add("Cheque / DD", p.cheque_no);
  add("Bank", p.bank_name);
  return lines.join("\n");
}
